import {
  StudentListArgumentInput,
  StudentListFilterInput,
  FunnelStateEnum,
} from "@/__generated__/types";
import { ParsedQs } from "qs";
import { z } from "zod/v4";
import { OrderingInput } from "@/types/StudentOrdering";
import { getDateString } from "@/util/dateUtils";

export const DEFAULT_CONTEXT: FilterContextType = "organization";

const CONTEXT_QUERY_KEY = "context";
const CONTEXT_DATA_QUERY_KEY = "contextData";

export type FilterContextType = keyof StudentListArgumentInput;

export const FilterContexts: (keyof StudentListArgumentInput)[] = [
  "bulkMessages",
  "channelOpens",
  "commitIndex",
  "communityTools",
  "directMessages",
  "feedPosts",
  "funnelChart",
  "groupChats",
  "liveEvent",
  "liveEvents",
  "mostEngaged",
  "organization",
  "profileViews",
  "rise",
  "roommateMatchFriendFinderSearches",
  "search",
  "rise",
];

const FilterContextData = z.union([
  z.object({
    funnelChartDate: z.coerce.date(),
    funnelChartFunnelStates: z.array(z.enum(FunnelStateEnum)).default([]),
  }),
  z.object({
    liveEventId: z.string(),
    liveEventFunnelStates: z.array(z.enum(FunnelStateEnum)).optional(),
  }),
]);

export type FilterContextData = z.infer<typeof FilterContextData>;

export function isFilterContextData(
  value: unknown,
): value is FilterContextData {
  return FilterContextData.safeParse(value).success;
}

export class FilterContext {
  readonly data: FilterContextType;
  readonly contextData: FilterContextData | undefined;

  constructor(
    context: FilterContextType = DEFAULT_CONTEXT,
    data: FilterContextData | undefined = undefined,
  ) {
    this.data = context;
    this.contextData = data;
  }

  static fromUrlQuery(parameters: ParsedQs) {
    const contextStr = parameters[CONTEXT_QUERY_KEY] as string;
    const contextDataStr = parameters[CONTEXT_DATA_QUERY_KEY];
    let context = undefined;
    let contextData = undefined;

    if (contextStr) {
      if (isFilterContext(contextStr)) {
        context = contextStr;
      } else {
        console.error(`Could not parse context from url query.`);
      }
    }

    if (contextDataStr) {
      const parseResult = FilterContextData.safeParse(contextDataStr);
      if (parseResult.success) {
        contextData = parseResult.data;
      } else {
        console.error(`Could not parse context data from url query.`);
      }
    }
    return new FilterContext(context, contextData);
  }

  toUrlQuery = () => ({
    [CONTEXT_QUERY_KEY]: this.data,
    [CONTEXT_DATA_QUERY_KEY]: this.contextData,
  });

  buildQueryArgument(
    filters: StudentListFilterInput | undefined,
    ordering: OrderingInput | undefined,
  ) {
    const baseQuery = {
      filters,
      ordering,
    };

    switch (this.data) {
      case "liveEvent":
        if (
          !this.contextData ||
          !("liveEventId" in this.contextData) ||
          !this.contextData.liveEventId
        ) {
          console.error("liveEventId is required for liveEvent context");
          return {
            [this.data]: baseQuery,
          };
        }
        return {
          liveEvent: {
            ...baseQuery,
            liveEventId: this.contextData.liveEventId,
            liveEventFunnelStates: this.contextData.liveEventFunnelStates,
          },
        };
      case "funnelChart":
        if (this.contextData && "funnelChartDate" in this.contextData)
          return {
            [this.data]: {
              ...baseQuery,
              ...this.contextData,
              funnelChartDate: getDateString(this.contextData.funnelChartDate),
            },
          };

        console.error("Could not parse funnelChart filter context data.");
        break;
      // Add other special contexts here as needed
      // case "bulkMessages":
      //   return { bulkMessages: { ...baseQuery, bulkMessageId: this.contextData.messageId } };
      default:
        return {
          [this.data]: {
            ...baseQuery,
            ...this.contextData,
          },
        };
    }
  }
}

export function isFilterContext(value: string): value is FilterContextType {
  return FilterContexts.includes(value as FilterContextType);
}
